import { useMemo } from 'react';
import { s } from '@/theme/tokens';
import { Box } from './Box';
import { resolveChartPlotAreaBackgroundColor } from './chartPlotAreaBackgroundColor';
import { useTheme } from './hooks';

type LineChartSkeletonProps = {
  height?: number;
  points?: number;
  leftInset?: string;
  rightInset?: string;
  topInset?: string;
  bottomInset?: string;
  strokeWidth?: number;
  showPlotAreaBackground?: boolean;
  hideYAxisLine?: boolean;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

export const LineChartSkeleton = ({
  height = 200,
  points = 7,
  leftInset,
  rightInset,
  topInset,
  bottomInset,
  strokeWidth = 1.4,
  showPlotAreaBackground = true,
  hideYAxisLine = false,
}: LineChartSkeletonProps): JSX.Element => {
  const theme = useTheme();
  const yAxisGutter = `calc(${s('xl')} + ${s('sm')})`;
  const chartTop = topInset ?? s('md');
  const chartRight = rightInset ?? s('lg');
  const chartBottom = bottomInset ?? `calc(${s('lg')} + ${s('xxs')})`;
  const chartInnerPaddingX = s('sm');
  const axisLeft = leftInset ?? `calc(${yAxisGutter} + ${chartInnerPaddingX})`;
  const axisBottom = chartBottom;
  const plotAreaBackgroundColor = resolveChartPlotAreaBackgroundColor(theme);

  const pointLayout = useMemo(() => {
    const resolvedPoints = clamp(points, 4, 16);
    const baseWave = [58, 56, 54, 57, 52, 49, 53, 50, 47, 45, 48, 55] as const;
    const coords = Array.from({ length: resolvedPoints }, (_, index) => {
      const x = resolvedPoints === 1 ? 50 : (index / (resolvedPoints - 1)) * 100;
      const sourceIndex = Math.round(
        (index / Math.max(resolvedPoints - 1, 1)) * (baseWave.length - 1),
      );
      const y = clamp(baseWave[sourceIndex] ?? 46, 10, 86);
      return { x, y };
    });
    const first = coords[0] as { x: number; y: number };
    const segments = coords.slice(1).map((point, index) => {
      const p0 = (coords[Math.max(0, index - 1)] ?? first) as { x: number; y: number };
      const p1 = (coords[index] ?? first) as { x: number; y: number };
      const p2 = point;
      const p3 = (coords[index + 2] ?? point) as { x: number; y: number };
      const tension = 0.22;
      const cp1x = p1.x + ((p2.x - p0.x) / 6) * tension * 6;
      const cp1y = p1.y + ((p2.y - p0.y) / 6) * tension * 6;
      const cp2x = p2.x - ((p3.x - p1.x) / 6) * tension * 6;
      const cp2y = p2.y - ((p3.y - p1.y) / 6) * tension * 6;
      return `C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    });
    const path = [`M ${first.x} ${first.y}`, ...segments].join(' ');
    return { path };
  }, [points]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      <Box
        sx={{
          position: 'absolute',
          left: axisLeft,
          right: chartRight,
          top: chartTop,
          bottom: axisBottom,
          backgroundColor: showPlotAreaBackground ? plotAreaBackgroundColor : 'transparent',
          px: chartInnerPaddingX,
          py: s('xxs'),
          opacity: hideYAxisLine ? 1 : 0.7,
        }}
      >
        <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path
            d={pointLayout.path}
            fill="none"
            stroke={theme.app.colors.border}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.9"
          />
        </svg>
      </Box>
    </Box>
  );
};
